import { useState, useEffect, useCallback } from 'react';
import { 
  subscribeToPushNotifications, 
  unsubscribeFromPushNotifications,
  registerBackgroundSync,
  isStandalone,
  getInstallationStatus
} from '../services/pwa/serviceWorkerRegistration';

interface PWAState {
  isOnline: boolean;
  isInstalled: boolean;
  isInstallable: boolean;
  hasUpdate: boolean;
  isPushSupported: boolean;
  isPushSubscribed: boolean;
  installationStatus: 'installed' | 'installable' | 'not-installable';
}

interface PWAActions {
  subscribeToPush: () => Promise<boolean>;
  unsubscribeFromPush: () => Promise<boolean>;
  registerSync: (tag: string) => Promise<void>;
  reloadApp: () => void;
}

export function usePWA(): PWAState & PWAActions {
  const [state, setState] = useState<PWAState>({
    isOnline: navigator.onLine,
    isInstalled: isStandalone(),
    isInstallable: false,
    hasUpdate: false,
    isPushSupported: 'serviceWorker' in navigator && 'PushManager' in window,
    isPushSubscribed: false,
    installationStatus: getInstallationStatus()
  });

  const [updateRegistration, setUpdateRegistration] = useState<ServiceWorkerRegistration | null>(null);

  // Check push subscription status
  const checkPushSubscription = useCallback(async () => {
    if (!state.isPushSupported) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setState(prev => ({ ...prev, isPushSubscribed: !!subscription }));
    } catch (error) {
      console.error('Error checking push subscription:', error);
    }
  }, [state.isPushSupported]);

  // Subscribe to push notifications
  const subscribeToPush = useCallback(async (): Promise<boolean> => {
    try {
      const subscription = await subscribeToPushNotifications();
      const success = !!subscription;
      setState(prev => ({ ...prev, isPushSubscribed: success }));
      return success;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return false;
    }
  }, []);

  // Unsubscribe from push notifications
  const unsubscribeFromPush = useCallback(async (): Promise<boolean> => {
    try {
      const success = await unsubscribeFromPushNotifications();
      setState(prev => ({ ...prev, isPushSubscribed: !success }));
      return success;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      return false;
    }
  }, []);

  // Register background sync
  const registerSync = useCallback(async (tag: string): Promise<void> => {
    try {
      await registerBackgroundSync(tag);
    } catch (error) {
      console.error('Error registering background sync:', error);
    }
  }, []);

  // Reload app with new service worker
  const reloadApp = useCallback(() => {
    if (updateRegistration?.waiting) {
      updateRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  }, [updateRegistration]);

  useEffect(() => {
    // Listen for online/offline events
    const handleOnline = () => setState(prev => ({ ...prev, isOnline: true }));
    const handleOffline = () => setState(prev => ({ ...prev, isOnline: false }));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setState(prev => ({ 
        ...prev, 
        isInstallable: true,
        installationStatus: 'installable'
      }));
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setState(prev => ({ 
        ...prev, 
        isInstalled: true,
        isInstallable: false,
        installationStatus: 'installed'
      }));
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check initial push subscription status
    checkPushSubscription();

    // Listen for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });

      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setState(prev => ({ ...prev, hasUpdate: true }));
                setUpdateRegistration(registration);
              }
            });
          }
        });
      });
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [checkPushSubscription]);

  return {
    ...state,
    subscribeToPush,
    unsubscribeFromPush,
    registerSync,
    reloadApp
  };
}